/*
 * dt-survey.js: one-question-at-a-time survey engine.
 *
 * Shared by sections/dt-survey.liquid (post-purchase voice-of-customer) and
 * sections/dt-review.liquid (post-delivery review). The sections render the
 * markup and the copy; this file only moves between steps, validates, keeps a
 * draft, and posts to heybar (survey_intake.js).
 *
 * Markup contract, all driven by data attributes so a section can add or
 * reorder questions without touching this file:
 *
 *   [data-dts]                    root. data-endpoint, data-survey, data-src-default
 *   [data-dts-step]               one step panel. Steps run in DOM order.
 *     [data-dts-optional]         on a step: Next is allowed with no answer
 *   [data-dts-order]              the order-number input (its step is required)
 *   [data-identity="optional"]    on the root: never block on identity at all
 *   [data-partial-save="on"]     on the root: POST after every step, keyed by a
 *                                 draft_id, so abandoned attempts are kept
 *   [data-dts-answer="<id>"]      a textarea/input whose value becomes answers[id]
 *   [data-dts-rating]             a radio group; checked value becomes its answer
 *   [data-dts-choice]             a button group; data-value becomes its answer
 *   [data-dts-next] [data-dts-back] [data-dts-submit]
 *   [data-dts-progress] [data-dts-progress-bar] [data-dts-count]
 *   [data-dts-error] [data-dts-thanks] [data-dts-hp]
 *
 * Draft answers persist to localStorage per survey so someone who gets
 * interrupted, or closes the tab on question four, does not have to start over.
 */
(function () {
  'use strict';

  function initSurvey(root) {
    if (!root || root.dataset.dtsReady === '1') return;
    root.dataset.dtsReady = '1';

    var endpoint = root.getAttribute('data-endpoint');
    var survey = root.getAttribute('data-survey') || 'voc';
    if (!endpoint) return;

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-dts-step]'));
    if (!steps.length) return;

    var orderInput = root.querySelector('[data-dts-order]');
    var honeypot = root.querySelector('[data-dts-hp]');
    var errorBox = root.querySelector('[data-dts-error]');
    var thanks = root.querySelector('[data-dts-thanks]');
    var form = root.querySelector('form');
    var bar = root.querySelector('[data-dts-progress-bar]');
    var counter = root.querySelector('[data-dts-count]');
    var backBtn = root.querySelector('[data-dts-back]');
    var nextBtn = root.querySelector('[data-dts-next]');
    var submitBtn = root.querySelector('[data-dts-submit]');
    var submitLabel = submitBtn ? submitBtn.textContent : 'Send';

    var askOrder = root.querySelector('[data-dts-ask-order]');

    // dt-review sets data-identity="optional": nothing on the page may ever
    // block. Almost everyone arrives from an email carrying ?e=, and a review
    // from someone we cannot match is still worth more than a review the person
    // abandoned at an identity gate. dt-survey.liquid leaves this unset and
    // keeps the old required-order behaviour.
    var identityOptional = root.getAttribute('data-identity') === 'optional';

    var params = new URLSearchParams(window.location.search);
    var DRAFT_KEY = 'dt_survey_draft_' + survey;
    var DRAFT_ID_KEY = 'dt_survey_draftid_' + survey;

    // Partial saves, opt-in per section via data-partial-save="on". The review
    // page turns it on: people abandon surveys, and what they typed before they
    // left is exactly the material we wanted. Every step posts the answers so
    // far under a draft_id the browser mints once per attempt, and the worker
    // rewrites the same row rather than piling up one row per keystroke. The
    // final submit posts the same shape without `partial`, which is what flips
    // completed to 1 server-side.
    var partialSave = root.getAttribute('data-partial-save') === 'on';
    var draftId = '';
    if (partialSave) {
      try {
        draftId = localStorage.getItem(DRAFT_ID_KEY) || '';
        if (!draftId) {
          draftId = (crypto && crypto.randomUUID)
            ? crypto.randomUUID()
            : String(Date.now()) + '-' + Math.random().toString(36).slice(2, 12);
          localStorage.setItem(DRAFT_ID_KEY, draftId);
        }
      } catch (e) {
        // Private mode with storage denied: fall back to a per-page-load id.
        // Worst case an attempt that spans a reload lands as two rows, which is
        // still better than losing the answers.
        draftId = String(Date.now()) + '-' + Math.random().toString(36).slice(2, 12);
      }
    }
    var lastSent = '';
    var index = 0;
    var sending = false;

    // How we know who this is, best first. A ?t= token is an opaque invite we
    // minted and emailed; it resolves server-side to the order and customer,
    // so nothing identifying rides in the URL. ?order= is the Shopify
    // confirmation email, where the order number is already in the body.
    // Only if we have neither do we show the order field and ask.
    var inviteToken = (params.get('t') || '').trim();
    // Read ?e= from the raw query string rather than URLSearchParams, which
    // decodes "+" as a space. Brevo has no url_encode filter, so a plus-addressed
    // address arrives literally as jane+shop@gmail.com and would otherwise become
    // "jane shop@gmail.com" and match nobody. Email addresses never contain
    // spaces, so treating "+" as a literal plus is always the right reading.
    var emailMatch = window.location.search.match(/[?&]e=([^&]*)/);
    var emailParam = '';
    if (emailMatch) {
      try {
        emailParam = decodeURIComponent(emailMatch[1].replace(/\+/g, '%2B')).trim();
      } catch (e) { emailParam = ''; }
    }
    var orderParam = (params.get('order') || params.get('order_number') || '').trim();
    var identityKnown = !!(inviteToken || emailParam || orderParam);

    // Take the address out of the visible URL straight away. It still reaches
    // our own endpoint in the POST body, but it stops being in the referrer
    // header sent to third-party scripts, in analytics page URLs, and in
    // anything the customer copies or shares from the address bar.
    if (emailParam) {
      try {
        var cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('e');
        window.history.replaceState({}, '', cleanUrl.toString());
      } catch (e) { /* old browser: leave it, the survey still works */ }
    }

    // ---- Reading answers out of a step ------------------------------------
    function fieldsIn(scope) {
      return Array.prototype.slice.call(
        scope.querySelectorAll('[data-dts-answer]')
      );
    }

    function valueOf(el) {
      if (el.hasAttribute('data-dts-rating')) {
        var checked = el.querySelector('input:checked');
        return checked ? checked.value : '';
      }
      if (el.hasAttribute('data-dts-choice')) {
        var picked = el.querySelector('[aria-pressed="true"]');
        return picked ? picked.getAttribute('data-value') : '';
      }
      return (el.value || '').trim();
    }

    function setValue(el, value) {
      if (el.hasAttribute('data-dts-rating')) {
        var input = el.querySelector('input[value="' + value + '"]');
        if (input) { input.checked = true; paintRating(el); }
        return;
      }
      if (el.hasAttribute('data-dts-choice')) {
        Array.prototype.forEach.call(el.querySelectorAll('[data-value]'), function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-value') === value ? 'true' : 'false');
        });
        return;
      }
      el.value = value;
    }

    function collect() {
      var answers = {};
      fieldsIn(root).forEach(function (el) {
        var v = valueOf(el);
        if (v) answers[el.getAttribute('data-dts-answer')] = v;
      });
      return answers;
    }

    // ---- Draft persistence -------------------------------------------------
    function saveDraft() {
      try {
        var draft = collect();
        if (orderInput && orderInput.value.trim()) draft.__order = orderInput.value.trim();
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch (e) { /* private mode, storage full: not worth breaking over */ }
    }

    function loadDraft() {
      var draft = {};
      try { draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}'); }
      catch (e) { return; }
      fieldsIn(root).forEach(function (el) {
        var key = el.getAttribute('data-dts-answer');
        if (draft[key] && !valueOf(el)) setValue(el, draft[key]);
      });
      if (orderInput && !orderInput.value && draft.__order) orderInput.value = draft.__order;
    }

    function clearDraft() {
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
      // Retire the id too, so someone who comes back and writes a second review
      // gets a new row instead of overwriting the one they already finished.
      try { localStorage.removeItem(DRAFT_ID_KEY); } catch (e) {}
    }

    // ---- Step navigation ---------------------------------------------------
    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = false;
    }
    function clearError() { if (errorBox) errorBox.hidden = true; }

    function render() {
      steps.forEach(function (step, i) { step.hidden = i !== index; });

      if (bar) bar.style.width = Math.round(((index + 1) / steps.length) * 100) + '%';
      if (counter) counter.textContent = (index + 1) + ' of ' + steps.length;
      if (backBtn) backBtn.hidden = index === 0;

      var last = index === steps.length - 1;
      if (nextBtn) nextBtn.hidden = last;
      if (submitBtn) submitBtn.hidden = !last;

      var focusable = steps[index].querySelector('textarea, input:not([type=radio]), [data-value]');
      if (focusable && !('ontouchstart' in window)) {
        // Autofocus helps on desktop. On touch it would pop the keyboard over
        // the question the person is still reading, so leave it alone there.
        try { focusable.focus({ preventScroll: true }); } catch (e) {}
      }
      steps[index].scrollIntoView({ block: 'nearest' });
    }

    function validateStep() {
      var step = steps[index];
      if (!identityOptional && !identityKnown && orderInput && step.contains(orderInput)) {
        if (!orderInput.value.trim()) {
          orderInput.setAttribute('aria-invalid', 'true');
          showError('Please add your order number so we can match this to your order.');
          orderInput.focus();
          return false;
        }
        orderInput.removeAttribute('aria-invalid');
      }
      return true;
    }

    function go(delta) {
      clearError();
      if (delta > 0 && !validateStep()) return;
      saveDraft();
      savePartial(false);
      index = Math.min(Math.max(index + delta, 0), steps.length - 1);
      render();
    }

    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); go(1); });
    if (backBtn) backBtn.addEventListener('click', function (e) { e.preventDefault(); go(-1); });

    // Enter advances from single-line inputs. Textareas keep Enter for newlines,
    // since these answers are the whole point and people write paragraphs.
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var t = e.target;
      if (t.tagName === 'TEXTAREA') return;
      if (t.tagName === 'INPUT' || t.hasAttribute('data-value')) {
        e.preventDefault();
        if (index === steps.length - 1) { submit(); } else { go(1); }
      }
    });

    // ---- Rating and choice widgets ----------------------------------------
    function paintRating(group) {
      var value = parseInt(valueOf(group), 10) || 0;
      Array.prototype.forEach.call(group.querySelectorAll('label[data-star]'), function (label) {
        var star = parseInt(label.getAttribute('data-star'), 10);
        label.classList.toggle('is-on', star <= value);
      });
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-dts-rating]'), function (group) {
      group.addEventListener('change', function () { paintRating(group); saveDraft(); });
      paintRating(group);
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-dts-choice]'), function (group) {
      group.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-value]');
        if (!btn || !group.contains(btn)) return;
        e.preventDefault();
        Array.prototype.forEach.call(group.querySelectorAll('[data-value]'), function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        saveDraft();
      });
    });

    fieldsIn(root).forEach(function (el) {
      if (el.hasAttribute('data-dts-rating') || el.hasAttribute('data-dts-choice')) return;
      el.addEventListener('input', saveDraft);
    });
    if (orderInput) orderInput.addEventListener('input', saveDraft);

    // ---- Payload -----------------------------------------------------------
    function payload(answers, isPartial) {
      var meta = {};
      params.forEach(function (value, key) {
        if (/^utm_/i.test(key) && value) meta[key] = value;
      });
      var order = orderInput ? orderInput.value.trim() : '';
      return {
        survey: survey,
        // The token wins: it identifies the order server-side, so the
        // customer never types anything and we never put them in a URL.
        token: inviteToken || undefined,
        email: inviteToken ? undefined : (emailParam || undefined),
        order: inviteToken ? undefined : (order || orderParam),
        src: params.get('src') || root.getAttribute('data-src-default') || 'direct',
        draft_id: draftId || undefined,
        partial: isPartial || undefined,
        answers: answers,
        meta: meta,
        website: honeypot ? honeypot.value : ''
      };
    }

    // ---- Partial save ------------------------------------------------------
    // Fire and forget, on every step change. Never blocks navigation, never
    // shows an error: this is a safety net behind the real submit, and a person
    // filling in a survey must not be told that a save they did not ask for
    // failed. Skipped while there is nothing to save, and skipped when nothing
    // has changed since the last one, so paging back and forth is not a write
    // per click.
    function savePartial(useBeacon) {
      if (!partialSave || !draftId) return;
      var answers = collect();
      if (!Object.keys(answers).length) return;
      var body = JSON.stringify(payload(answers, true));
      if (body === lastSent) return;
      lastSent = body;
      try {
        if (useBeacon && navigator.sendBeacon) {
          // text/plain on purpose. It is a CORS-safelisted content type, so the
          // beacon goes straight out; application/json would force a preflight,
          // and a preflight cannot complete on a page that is being torn down.
          // The worker reads request.text() and JSON.parses it, so the header
          // it arrives under does not matter.
          navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
          return;
        }
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body,
          keepalive: true
        }).catch(function () { lastSent = ''; });
      } catch (e) { lastSent = ''; }
    }

    // The last thing they typed is the thing most likely to be lost, so flush
    // once more as the tab goes away. pagehide fires on mobile Safari where
    // unload does not; visibilitychange catches an app switch that never comes
    // back. Both use sendBeacon, which survives the page being torn down.
    if (partialSave) {
      window.addEventListener('pagehide', function () { savePartial(true); });
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') savePartial(true);
      });
    }

    // ---- Submit ------------------------------------------------------------
    function submit() {
      if (sending) return;
      clearError();
      if (!validateStep()) return;

      var order = orderInput ? orderInput.value.trim() : '';
      if (!identityOptional && !identityKnown && !order) {
        // The order step is behind us, so send them back rather than failing
        // at the server with a message they cannot act on from here.
        index = steps.findIndex(function (s) { return orderInput && s.contains(orderInput); });
        if (index < 0) index = 0;
        render();
        showError('Please add your order number so we can match this to your order.');
        return;
      }

      var answers = collect();
      if (!Object.keys(answers).length) {
        showError('Please answer at least one question. Even a single sentence helps.');
        return;
      }

      sending = true;
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending...'; }

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload(answers, false))
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok || !data.ok) throw new Error(data.error || 'Something went wrong on our end.');
          return data;
        });
      }).then(function () {
        clearDraft();
        if (form) form.hidden = true;
        var chrome = root.querySelector('[data-dts-progress]');
        if (chrome) chrome.hidden = true;
        if (thanks) {
          thanks.hidden = false;
          thanks.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }).catch(function (err) {
        // The draft survives on purpose so they can just press send again.
        sending = false;
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitLabel; }
        showError((err && err.message) || 'Could not send right now. Please try again.');
      });
    }

    if (submitBtn) submitBtn.addEventListener('click', function (e) { e.preventDefault(); submit(); });
    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });

    // ---- Boot --------------------------------------------------------------
    if (orderParam && orderInput) orderInput.value = orderParam;
    loadDraft();

    // Step 1 is never skipped now: it carries the intro and the prize, which
    // is the reason most people keep going. What changes is whether it also
    // has to ask who they are.
    if (!identityKnown) {
      if (askOrder) askOrder.hidden = false;
      if (orderInput && orderInput.value.trim()) identityKnown = true;
    }
    render();

    // ---- Query-string passthrough (.cursorrules standing rule) -------------
    // Generic, never an allowlist. `view` excluded because it is Shopify's
    // template-preview switch. Params already in the href win. Hash links and
    // off-site links skipped. Scoped to the section root so every anchor is
    // covered rather than one button class.
    var carry = [];
    params.forEach(function (value, key) {
      if (!value) return;
      var lower = key.toLowerCase();
      if (lower === 'view') return;   // Shopify's template-preview switch
      if (lower === 'e') return;      // never forward the address onward
      carry.push([key, value]);
    });
    if (!carry.length) return;
    Array.prototype.forEach.call(root.querySelectorAll('a[href]'), function (anchor) {
      var href = anchor.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#') return;
      try {
        var target = new URL(href, window.location.origin);
        if (target.origin !== window.location.origin) return;   // never leak off-site
        carry.forEach(function (pair) {
          if (!target.searchParams.has(pair[0])) target.searchParams.set(pair[0], pair[1]);
        });
        anchor.setAttribute('href', target.toString());
      } catch (error) { /* malformed href: leave it as authored */ }
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-dts]'), initSurvey);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Shopify theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
